import { useState, useEffect, useCallback } from 'react';
import { useThemeMode, THEME_MODES } from '../../theme/ThemeProvider';
import userPreferencesService from '../services/userPreferencesService';

/**
 * Hook personalizado para gerenciar preferências de tema do usuário
 * @param {string} userId - ID único do usuário
 * @returns {Object} Objeto com funções e estado das preferências
 */
export const useThemePreferences = (userId) => {
  const { changeTheme, currentTheme } = useThemeMode();
  const [userPreferences, setUserPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  /**
   * Carrega as preferências do usuário ao inicializar
   */
  const loadUserPreferences = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const preferences = await userPreferencesService.getPreferences(userId);
      
      if (preferences) {
        setUserPreferences(preferences);
        // Aplica o tema salvo apenas na primeira carga
        if (isFirstLoad) {
          changeTheme(preferences.theme_mode);
          setIsFirstLoad(false);
        }
        // Tema carregado para usuário
      } else {
        // Se não houver preferências, usa o tema padrão
        setUserPreferences({
          userId,
          theme_mode: THEME_MODES.DEFAULT,
          lastUpdated: new Date().toISOString(),
          additionalPreferences: {},
          version: '1.0.0'
        });
        if (isFirstLoad) {
          changeTheme(THEME_MODES.DEFAULT);
          setIsFirstLoad(false);
        }
        // Usando tema padrão para usuário
      }
    } catch (err) {
      setError(err.message);
      // Erro ao carregar preferências
    } finally {
      setIsLoading(false);
    }
  }, [userId, changeTheme, isFirstLoad]);

  /**
   * Salva as preferências do usuário
   */
  const saveUserPreferences = useCallback(async (themeMode, additionalPreferences = {}) => {
    if (!userId) {
      throw new Error('ID do usuário é obrigatório');
    }

    try {
      setError(null);
      
      // Aplicar tema imediatamente para evitar piscar
      changeTheme(themeMode);
      
      // Salvar preferências usando o novo serviço
      const success = await userPreferencesService.savePreferences(userId, {
        theme_mode: themeMode,
        additional_preferences: additionalPreferences
      });

      if (success) {
        // Atualizar estado local imediatamente
        setUserPreferences(prev => ({
          ...prev,
          theme_mode: themeMode,
          additional_preferences: additionalPreferences,
          ts_updated: new Date().toISOString()
        }));
        
        // Preferências salvas para usuário
        return true;
      } else {
        // Se falhou, reverter o tema
        changeTheme(userPreferences?.theme_mode || 'default');
        throw new Error('Falha ao salvar preferências');
      }
    } catch (err) {
      setError(err.message);
      // Erro ao salvar preferências
      // Reverter o tema em caso de erro
      changeTheme(userPreferences?.theme_mode || 'default');
      return false;
    }
  }, [userId, changeTheme, userPreferences?.theme_mode]);

  /**
   * Atualiza apenas o tema (otimizado para performance)
   */
  const updateTheme = useCallback(async (newThemeMode) => {
    if (!userId) {
      return false;
    }

    try {
      setError(null);
      
      // Salvar preferências usando o serviço otimizado
      const success = await userPreferencesService.updateTheme(userId, newThemeMode);

      if (success) {
        // Atualizar estado local imediatamente
        setUserPreferences(prev => ({
          ...prev,
          theme_mode: newThemeMode,
          ts_updated: new Date().toISOString()
        }));
        
        // Tema atualizado para usuário
        return true;
      } else {
        throw new Error('Falha ao atualizar tema');
      }
    } catch (err) {
      setError(err.message);
      // Erro ao atualizar tema
      return false;
    }
  }, [userId]);

  /**
   * Atualiza preferências adicionais
   */
  const updateAdditionalPreferences = useCallback(async (newAdditionalPreferences) => {
    if (!userPreferences) {
      return await saveUserPreferences(THEME_MODES.DEFAULT, newAdditionalPreferences);
    }
    
    return await saveUserPreferences(userPreferences.theme_mode, newAdditionalPreferences);
  }, [userPreferences, saveUserPreferences]);

  /**
   * Remove as preferências do usuário
   */
  const removeUserPreferences = useCallback(async () => {
    if (!userId) {
      throw new Error('ID do usuário é obrigatório');
    }

    try {
      // Remove do localStorage (fallback)
      const storageKey = `user_preferences_${userId}`;
      localStorage.removeItem(storageKey);
      
      setUserPreferences(null);
      // Volta para o tema padrão
      changeTheme(THEME_MODES.DEFAULT);
      // Preferências removidas para usuário
      return true;
    } catch (err) {
      setError(err.message);
      // Erro ao remover preferências
      return false;
    }
  }, [userId, changeTheme]);

  /**
   * Salvar foto do usuário
   */
  const saveUserPhoto = useCallback(async (photoBase64, metadata = {}) => {
    try {
      const success = await userPreferencesService.saveUserPhoto(userId, photoBase64, metadata);
      if (success) {
        // Recarrega as preferências para incluir a foto
        await loadUserPreferences();
        return true;
      }
      return false;
    } catch (error) {
      // Erro ao salvar foto
      return false;
    }
  }, [userId, loadUserPreferences]);

  /**
   * Remover foto do usuário
   */
  const removeUserPhoto = useCallback(async () => {
    try {
      const success = await userPreferencesService.removeUserPhoto(userId);
      if (success) {
        // Recarrega as preferências
        await loadUserPreferences();
        return true;
      }
      return false;
    } catch (error) {
      // Erro ao remover foto
      return false;
    }
  }, [userId, loadUserPreferences]);

  /**
   * Reseta as preferências para o padrão
   */
  const resetToDefault = useCallback(async () => {
    try {
      const success = await userPreferencesService.resetToDefault(userId);
      if (success) {
        // Recarrega as preferências para obter os dados atualizados da API
        await loadUserPreferences();
        return true;
      }
      return false;
    } catch (error) {
      // Erro ao resetar preferências
      return false;
    }
  }, [userId, loadUserPreferences]);

  /**
   * Carrega preferências ao montar o componente ou mudar o userId
   */
  useEffect(() => {
    loadUserPreferences();
  }, [loadUserPreferences]);

  // Removido useEffect problemático - sincronização agora é feita apenas na primeira carga

  return {
    // Estado
    userPreferences,
    isLoading,
    error,
    
    // Funções
    loadUserPreferences,
    saveUserPreferences,
    updateTheme,
    updateAdditionalPreferences,
    removeUserPreferences,
    resetToDefault,
    saveUserPhoto,
    removeUserPhoto,
    
    // Utilitários
    hasPreferences: !!userPreferences,
    currentTheme: userPreferences?.theme_mode || currentTheme,
    lastUpdated: userPreferences?.ts_updated || userPreferences?.lastUpdated,
    additionalPreferences: userPreferences?.additional_preferences || userPreferences?.additionalPreferences || {},
    userPhoto: userPreferences?.user_photo || null,
    photoMetadata: userPreferences?.photo_metadata || null
  };
};

export default useThemePreferences;

